import * as React from 'react';
import { RotateCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import {
  useAdminGenerateApplePayCsr,
  useAdminUploadAppleCertificate,
  useAdminUploadApplePayCertificates,
} from '@/lib/api/apple-pay-registrations';
import {
  CsrCertificateSetup,
  downloadCsrFile,
  type CsrSetupState,
} from '@/components/apple-pay/CsrCertificateSetup';
import { P12UploadForm, type P12UploadPayload } from '@/components/apple-pay/P12UploadForm';
import { ExpiryBadge, type ApplePayRegistration } from './utils';

const BT_MAX_ACTIVE_CERTIFICATES = 2;

export function CertificatesSection({ registration }: { registration: ApplePayRegistration }) {
  const generateCsr = useAdminGenerateApplePayCsr();
  const uploadCert = useAdminUploadAppleCertificate();
  const uploadP12 = useAdminUploadApplePayCertificates();
  const [rotateTarget, setRotateTarget] = React.useState<string | null>(null);
  // 'csr' = guided flow (we hold the keys); 'p12' = certs + keys already in hand.
  const [mode, setMode] = React.useState<'csr' | 'p12'>('csr');

  const certs = registration.certificates;
  const setup = (registration.certificateSetup ?? []) as CsrSetupState[];
  const setupInProgress = setup.length > 0;
  const busy = generateCsr.isPending || uploadCert.isPending || uploadP12.isPending;

  const handleDownloadCsr = async (type: CsrSetupState['type']) => {
    const result = await generateCsr.mutateAsync({
      registrationId: registration.id,
      certificateType: type,
    });
    downloadCsrFile(result.csrPem, result.fileName);
  };

  const handleUploadCert = async (type: CsrSetupState['type'], certificateBase64: string) => {
    const result = await uploadCert.mutateAsync({
      registrationId: registration.id,
      certificateType: type,
      certificate: certificateBase64,
      ...(rotateTarget ? { replaceCertificateId: rotateTarget } : {}),
    });
    if (result.finalized) setRotateTarget(null);
  };

  const handleP12Submit = async (payload: P12UploadPayload) => {
    await uploadP12.mutateAsync({
      registrationId: registration.id,
      ...payload,
      ...(rotateTarget ? { replaceCertificateId: rotateTarget } : {}),
    });
    setRotateTarget(null);
  };

  const showSetup = certs.length === 0 || rotateTarget !== null || setupInProgress;

  return (
    <section className="space-y-4">
      <h4 className="text-sm font-semibold">
        Certificates ({certs.length}/{BT_MAX_ACTIVE_CERTIFICATES})
      </h4>

      {certs.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Certificate</TableHead>
                <TableHead>Payment cert expiry</TableHead>
                <TableHead>Identity cert expiry</TableHead>
                <TableHead className="w-[140px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {certs.map((cert) => (
                <TableRow key={cert.id}>
                  <TableCell className="max-w-[180px] truncate font-mono text-xs">
                    {cert.btCertificateId}
                  </TableCell>
                  <TableCell>
                    <ExpiryBadge value={cert.paymentCertExpiresAt} />
                  </TableCell>
                  <TableCell>
                    <ExpiryBadge value={cert.identityCertExpiresAt} />
                  </TableCell>
                  <TableCell className="text-right">
                    {rotateTarget === cert.id ? (
                      <Button variant="outline" size="sm" onClick={() => setRotateTarget(null)}>
                        <X className="mr-1 h-3.5 w-3.5" />
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={rotateTarget !== null}
                        onClick={() => setRotateTarget(cert.id)}
                      >
                        <RotateCw className="mr-1 h-3.5 w-3.5" />
                        Rotate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {showSetup ? (
        <div className="space-y-2">
          {mode === 'csr' ? (
            <>
              <CsrCertificateSetup
                setup={setup}
                busy={busy}
                replacing={rotateTarget !== null}
                onDownloadCsr={handleDownloadCsr}
                onUploadCert={handleUploadCert}
              />
              <Button
                variant="link"
                size="sm"
                className="text-muted-foreground h-auto px-0 text-xs"
                onClick={() => setMode('p12')}
              >
                Already have certificates (with their private keys)? Upload .p12 files instead
              </Button>
            </>
          ) : (
            <>
              <P12UploadForm busy={busy} replacing={rotateTarget !== null} onSubmit={handleP12Submit} />
              <Button
                variant="link"
                size="sm"
                className="text-muted-foreground h-auto px-0 text-xs"
                onClick={() => setMode('csr')}
              >
                Only have .cer files or nothing yet? Use the guided signing-request flow instead
              </Button>
            </>
          )}
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">
          Use <strong>Rotate</strong> on a certificate to replace it (BT allows at most{' '}
          {BT_MAX_ACTIVE_CERTIFICATES} active certificates).
        </p>
      )}
    </section>
  );
}
